/**
 * Inquiries Desk → an account deletion request (category 'account_deletion').
 * The customer asked from Profile while something was still open. Once the
 * team has settled it (trip cancelled or flown, credit handled under the
 * Terms), this finishes the deletion: POST /api/admin/account-deletion.
 * The server refuses while anything is still open and says what.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { UserX, Loader2 } from 'lucide-react';

export default function AccountDeletionCard({ inquiry, buildAuthHeader, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setWorking(true);
    setError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch('/api/admin/account-deletion', {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: inquiry.user_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      onDeleted(inquiry.id);
    } catch (e) {
      setError(e?.message || 'Deletion failed.');
      setConfirming(false);
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="rounded-2xl border border-red-400/20 bg-red-500/[0.05] p-3.5" data-testid="account-deletion-card">
      <div className="flex items-center gap-2 mb-1.5">
        <UserX className="w-3.5 h-3.5 text-red-200/70" strokeWidth={1.75} />
        <h3 className="text-[10.5px] uppercase tracking-wider font-semibold text-red-200/70">Account deletion</h3>
      </div>
      <p className="text-[12.5px] text-white/60 leading-snug">
        Settle what&rsquo;s still open with the customer first: cancel or finish the trip, and handle any Defera Credit. Then delete the account. This can&rsquo;t be undone.
      </p>
      {error && <p role="alert" className="mt-2 rounded-lg border border-red-500/20 bg-red-500/10 px-2.5 py-1.5 text-[12px] text-red-200/90">{error}</p>}
      {confirming ? (
        <div className="mt-2.5 flex items-center gap-2">
          <button type="button" onClick={() => setConfirming(false)} disabled={working} className="text-[12px] text-white/55 hover:text-white">Keep</button>
          <button
            type="button"
            onClick={run}
            disabled={working}
            className="rounded-full border border-red-400/40 bg-red-600/80 hover:bg-red-600 px-3 py-1 text-[12px] font-medium text-white disabled:opacity-60"
          >
            {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Yes, delete the account'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-2.5 rounded-full border border-red-400/30 px-3 py-1 text-[12px] font-medium text-red-200 hover:bg-red-500/10"
        >
          Delete account
        </button>
      )}
    </section>
  );
}
AccountDeletionCard.propTypes = {
  inquiry: PropTypes.shape({ id: PropTypes.string, user_id: PropTypes.string }).isRequired,
  buildAuthHeader: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};
